import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import EmailIcon from '@mui/icons-material/Email';
import LoginIcon from '@mui/icons-material/Login';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AxymaLogo from '../../assets/images/Axyma_branco.svg';
import Logo from '../../assets/images/logo-login.png';
import Loading from '../Loading/Loading';
import { AuthContext } from '../../context/AuthContext';
import { UserContext } from '../../context/UserContext';
import { trackEventMatomo } from '../../helpers/matomo';
import Login from '../../service/Login';
import { LoginDiscovery } from '../../types/auth';
import Translation from '../Translation/Translation';
import { Box, SmallText } from './FormLoginStyle';

const loginService = new Login();
const resetUrl = `${window.location.origin}/resetPassword`;

function FormLogin() {
  const { discoverLoginMethods, loginWithPassword, startSso } = useContext(AuthContext);
  const { openModal, popUp } = useContext(UserContext);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [discovery, setDiscovery] = useState<LoginDiscovery | null>(null);
  const [busy, setBusy] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [resetValue, setResetValue] = useState('');
  const [loadingReset, setLoadingReset] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('translation');

  const continueDiscovery = async () => {
    setBusy(true);
    try { setDiscovery(await discoverLoginMethods(login.trim())); }
    catch { setDiscovery({ methods: [{ type: 'password', enabled: true }] }); }
    finally { setBusy(false); }
  };

  const loginPassword = async () => {
    setBusy(true);
    setIsAuthenticating(true);

    try {
      await loginWithPassword(login.trim(), password);
      setPassword('');
      const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
      navigate(returnTo?.startsWith('/') ? returnTo : '/home', { replace: true });
    } catch {
      setPassword('');
      toast.error('Não foi possível concluir o login. Verifique os dados e tente novamente.');
    } finally {
      setBusy(false);
      setIsAuthenticating(false);
    }
  };

  const hasMethod = (type: 'password' | 'google' | 'microsoft') => Boolean(discovery?.methods.some((method) => method.type === type && method.enabled));
  const canStartSso = Boolean(discovery?.empresa?.id);
  const now = new Date();

  if (isAuthenticating) {
    return (
      <div
        style={{
          backgroundColor: '#3C2864',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'space-evenly',
        }}
      >
        <img src={AxymaLogo} alt="logo axyma branco" width="40%" />
        <Loading />
      </div>
    );
  }

  return <Stack width="100%" height="100svh" direction="row" sx={{ justifyContent: 'center', alignItems: 'center', background: 'linear-gradient(to right, #9a72c9, #612a79)' }}>
    <Box><Stack direction="column" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', color: 'white' }} width="250px">
      <Translation />
      <img width="150px" alt="Axyma" src={Logo} style={{ borderRadius: '5px', filter: 'contrast(150%)' }} />
      <Typography color="white" variant="body1" fontWeight={600}>{t('loginScreen.welcomelogin')}</Typography>
      <form onSubmit={(event) => { event.preventDefault(); trackEventMatomo('login', 'click', 'form', 'login'); discovery ? void loginPassword() : void continueDiscovery(); }} autoComplete="off">
        <Stack spacing={1.5}>
          <TextField disabled={busy} variant="filled" InputProps={{ style: { backgroundColor: 'white' } }} size="small" required label={t('loginScreen.username')} value={login} onChange={(event) => { setLogin(event.target.value); setDiscovery(null); }} />
          {hasMethod('password') && <TextField disabled={busy} variant="filled" InputProps={{ style: { backgroundColor: 'white' } }} size="small" required fullWidth type="password" label={t('loginScreen.password')} value={password} onChange={(event) => setPassword(event.target.value)} />}
          <Button disabled={busy || !login || (discovery !== null && hasMethod('password') && !password)} endIcon={<LoginIcon />} variant="contained" type="submit">
            {busy ? 'Aguarde…' : discovery ? t('loginScreen.loginbtn') : 'Continuar'}
          </Button>
          {hasMethod('google') && <Button disabled={busy || !canStartSso} variant="outlined" color="inherit" onClick={() => discovery?.empresa && void startSso('google', discovery.empresa.id)}>Continuar com Google</Button>}
          {hasMethod('microsoft') && <Button disabled={busy || !canStartSso} variant="outlined" color="inherit" onClick={() => discovery?.empresa && void startSso('microsoft', discovery.empresa.id)}>Continuar com Microsoft</Button>}
        </Stack>
      </form>
      {hasMethod('password') && <Button sx={{ color: 'white', fontSize: '9px !important' }} size="small" variant="text" onClick={() => openModal()}>{t('loginScreen.forgot')}</Button>}
      <div className={`modal ${popUp ? 'modal is-active' : 'modal'}`}><div className="modal-background" /><div className="modal-card"><header className="modal-card-head"><Button startIcon={<ArrowCircleLeftIcon />} size="small" onClick={() => openModal()}>{t('return')}</Button></header>
        <form onSubmit={async (event) => { event.preventDefault(); setLoadingReset(true); try { await loginService.sendEmailReset({ login: resetValue, lastPasswordRequired: false, url: resetUrl }); toast.success(t('toast.emailSentSuccessfully')); openModal(); } finally { setLoadingReset(false); } }}>
          <div className="modal-card-body"><TextField fullWidth required label={t('loginScreen.inputModal')} value={resetValue} onChange={(event) => setResetValue(event.target.value)} /></div>
          <footer className="modal-card-foot"><Button type="submit" endIcon={<EmailIcon />} disabled={loadingReset}>{t('loginScreen.sendModal')}</Button></footer>
        </form></div></div>
      <SmallText>Axyma @ {now.getFullYear()}</SmallText>
    </Stack></Box>
  </Stack>;
}
export default FormLogin;
